import { isUndefined } from 'lodash-es';
import * as t from 'io-ts';

import type {
    InteractiveTitleSelectDefinition,
    InteractiveTitleSettingDefinition,
    SelectDefinition,
} from 'header/types';

import { define, createSpecSettingsInterface } from './_common';

export const SCALE_OPTIONS = [
    { key: '1', label: 'None' },
    { key: '1000', label: 'Thousands' },
    { key: '1000000', label: 'Millions' },
    { key: '1000000000', label: 'Billions' },
    { key: '1000000000000', label: 'Trillions' },
] as const;

const SHORT_LABELS: Record<string, string> = {
    '1': '-',
    '1000': 'Tsd.',
    '1000000': 'Mio.',
    '1000000000': 'Bn.',
    '1000000000000': 'Tr.',
};

export interface ScaleSettingDefinition extends SelectDefinition {
    key: 'SCALE';
}

const definition: ScaleSettingDefinition = {
    type: 'Select',
    key: 'SCALE',
    label: 'Scale',
    options: [...SCALE_OPTIONS],
    value: '1',
};

const specSettingsInterface = createSpecSettingsInterface(
    definition,
    t.number,
    t.partial({
        scaleOptions: t.array(t.number),
    }),
);

export const codec = specSettingsInterface.input;

export const ScaleSettingDefinition = define({
    key: 'SCALE',
    label: 'scale',
    definition,
    fromSetting: settings => {
        const value = specSettingsInterface.extractValue(settings.scale);
        const valueKey = String(value);

        if (isUndefined(value)) return undefined;
        if (!specSettingsInterface.isObjectNotation(settings.scale)) return valueKey;

        const keyWhiteList = validateUserScaleOptions(settings.scale.scaleOptions);

        return keyWhiteList.includes(valueKey) ? valueKey : keyWhiteList[0];
    },
    createDefinitionWithOpts: (opts = {}) => ({ ...definition, ...opts }),
    createDefinitionFromSpec: settings => {
        if (isUndefined(settings.scale)) return undefined;
        if (!specSettingsInterface.isObjectNotation(settings.scale)) return definition;

        const keyWhiteList = validateUserScaleOptions(settings.scale.scaleOptions);

        const options = definition.options
            .filter(({ key }) => keyWhiteList.includes(key))
            .map(({ key, label }) => ({
                key,
                label: settings.shortLabels ? SHORT_LABELS[key] : label,
            }));

        return {
            ...definition,
            ...specSettingsInterface.createDefinitionWithSettings(settings.scale),
            options,
        };
    },
    applySettingDependency: ({ diff, view, settings }) => {
        const viewChanged = !!diff.VIEWID && !!diff.VIEWID.previous;
        const currentScale = settings.SCALE;

        if (!viewChanged || !currentScale) return;
        if (!view || !view.settings || !specSettingsInterface.isObjectNotation(view.settings.scale))
            return;

        // in case the view has changed, and it has `settings.scale.scaleOptions` specified
        // we have to validate that the current setting still applies, if not we unset it

        const keyWhiteList = validateUserScaleOptions(view.settings.scale.scaleOptions);

        return keyWhiteList.includes(currentScale) ? undefined : { value: undefined };
    },
    // for scale with value 1 (no scaling) we want to display empty string instead of `None`
    replacementTags: definition => {
        const replacementTag =
            definition.value === '1'
                ? ''
                : definition.options.find(({ key }) => key === definition.value)?.label ||
                  definition.value;
        return {
            SCALE: replacementTag,
        };
    },
    interactiveTags: definition => {
        const interactiveTag: InteractiveTitleSettingDefinition<
            SelectDefinition,
            InteractiveTitleSelectDefinition
        > = {
            setting: definition,
            // we are passing selected value ('') to native select component which doesn't exist in options,
            // thus in select dropdown UI first value looks like selected thats why we need to make sure that
            // value 'None' is the first one of options
            field: {
                ...definition,
                value: definition.value === '1' ? '' : definition.value,
                options: [...definition.options].sort(
                    (a, b) => Number.parseInt(a.key) - Number.parseInt(b.key),
                ),
            },
            format: value => ({ ...definition, value }),
        };

        return {
            SCALE: interactiveTag,
        };
    },

    toSignals: definition => {
        const selectedOption = definition.options.find(({ key }) => key === definition.value);
        const label = selectedOption ? selectedOption.label : definition.value;

        return [
            { name: definition.key, value: definition.value },
            { name: `${definition.key}_LABEL`, value: label },
        ];
    },
});

function validateUserScaleOptions(options?: number[]): string[] {
    const allAllowedValues = definition.options.map(({ key }) => key);

    if (!options) return definition.options.map(({ key }) => key);

    const userValues = options.map(String).filter(key => allAllowedValues.includes(key));

    return userValues.length === 0 ? allAllowedValues : userValues;
}
